'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Upload, ChevronDown, Star, Loader2, Check,  } from 'lucide-react';
import { toast } from 'sonner';
import { StoredTrade, addTradeAsync, updateTradeAsync, generateTradeId } from '../utils/tradeStorage';

interface LogTradeModalProps {
  open: boolean;
  onClose: () => void;
  editTrade?: StoredTrade | null;
  onSaved?: (trade: StoredTrade) => void;
}

interface TradeFormData {
  symbol: string;
  direction: 'Long' | 'Short';
  date: string;
  entryPrice: string;
  exitPrice: string;
  positionSize: string;
  stopLoss: string;
  takeProfit: string;
  strategy: string;
  timeframe: string;
  notes: string;
  lessons: string;
  rating: number;
}

const strategies = ['Breakout', 'Momentum', 'VWAP Reclaim', 'Gap Fill', 'Mean Reversion', 'Scalp', 'Swing', 'Other'];
const timeframes = ['1m', '5m', '15m', '30m', '1H', '4H', '1D', '1W'];

export default function LogTradeModal({ open, onClose, editTrade, onSaved }: LogTradeModalProps) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<TradeFormData>({
    defaultValues: { direction: 'Long', date: '2026-08-25' },
  });

  // Populate form when editing an existing trade
  useEffect(() => {
    if (editTrade && open) {
      reset({
        symbol: editTrade.symbol,
        direction: editTrade.direction,
        date: editTrade.dateISO,
        entryPrice: String(editTrade.entry),
        exitPrice: String(editTrade.exit),
        positionSize: String(editTrade.size),
        stopLoss: editTrade.stopLoss ? String(editTrade.stopLoss) : '',
        takeProfit: editTrade.takeProfit ? String(editTrade.takeProfit) : '',
        strategy: editTrade.strategy,
        timeframe: editTrade.timeframe || '15m',
        notes: editTrade.notes || '',
        lessons: editTrade.lessons || '',
        rating: editTrade.rating,
      });
      setRating(editTrade.rating);
    } else if (!editTrade && open) {
      reset({ direction: 'Long', date: new Date().toISOString().slice(0, 10) });
      setRating(0);
    }
  }, [editTrade, open, reset]);

  const entryPrice = watch('entryPrice');
  const exitPrice = watch('exitPrice');
  const positionSize = watch('positionSize');
  const direction = watch('direction');

  const calcPnl = () => {
    const entry = parseFloat(entryPrice);
    const exit = parseFloat(exitPrice);
    const size = parseFloat(positionSize);
    if (!entry || !exit || !size) return null;
    const diff = direction === 'Long' ? exit - entry : entry - exit;
    return (diff * size).toFixed(2);
  };

  const pnl = calcPnl();

  const onSubmit = async (data: TradeFormData) => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 600));

    const entry = parseFloat(data.entryPrice);
    const exit = parseFloat(data.exitPrice);
    const size = parseFloat(data.positionSize);
    const diff = data.direction === 'Long' ? exit - entry : entry - exit;
    const pnl = parseFloat((diff * size).toFixed(2));
    const pnlPct = parseFloat(((diff / entry) * 100).toFixed(2));
    const stopLossVal = data.stopLoss ? parseFloat(data.stopLoss) : undefined;
    const tpVal = data.takeProfit ? parseFloat(data.takeProfit) : undefined;
    const riskPerShare = stopLossVal ? Math.abs(entry - stopLossVal) : 1;
    const rewardPerShare = tpVal ? Math.abs(tpVal - entry) : Math.abs(diff);
    const rr = parseFloat((rewardPerShare / riskPerShare).toFixed(1));

    const dateObj = new Date(data.date);
    const dateLabel = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    const savedTrade: StoredTrade = {
      id: editTrade ? editTrade.id : generateTradeId(),
      date: dateLabel,
      dateISO: data.date,
      symbol: data.symbol.toUpperCase(),
      direction: data.direction,
      entry,
      exit,
      size,
      pnl,
      pnlPct,
      rr,
      strategy: data.strategy,
      timeframe: data.timeframe,
      stopLoss: stopLossVal,
      takeProfit: tpVal,
      rating: rating || 3,
      hasScreenshot: screenshots.length > 0,
      notes: data.notes || '',
      lessons: data.lessons || '',
      status: 'Pending Review',
    };

    if (editTrade) {
      await updateTradeAsync(savedTrade);
      onSaved?.(savedTrade);
      toast.success(`Trade ${savedTrade.symbol} updated`);
    } else {
      await addTradeAsync(savedTrade);
      onSaved?.(savedTrade);
      toast.success(`Trade ${savedTrade.symbol} logged successfully`);
    }

    setSubmitting(false);
    reset();
    setRating(0);
    setScreenshots([]);
    setStep(1);
    onClose();
  };

  const handleClose = () => {
    reset();
    setRating(0);
    setScreenshots([]);
    setStep(1);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-base font-600 text-foreground">{editTrade ? 'Edit Trade' : 'Log New Trade'}</h2>
            <p className="text-xs text-muted-foreground">Step {step} of 2 — {step === 1 ? 'Trade Details' : 'Notes & Review'}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Step indicator */}
            <div className="flex items-center gap-1.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-600 ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>1</div>
              <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-primary' : 'bg-border'}`} />
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-600 ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>2</div>
            </div>
            <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors ml-2">
              <X size={16} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {step === 1 && (
            <div className="px-6 py-5 space-y-5">
              {/* Symbol + Direction */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-500 text-foreground mb-1.5">
                    Symbol <span className="text-negative">*</span>
                  </label>
                  <input
                    {...register('symbol', { required: 'Symbol is required' })}
                    placeholder="e.g. NVDA"
                    className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60 uppercase font-mono-nums transition-colors"
                  />
                  {errors.symbol && <p className="text-xs text-negative mt-1">{errors.symbol.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-500 text-foreground mb-1.5">Direction <span className="text-negative">*</span></label>
                  <div className="flex rounded-lg overflow-hidden border border-border">
                    {(['Long', 'Short'] as const).map((dir) => (
                      <label
                        key={`dir-${dir}`}
                        className={`flex-1 text-center py-2.5 text-sm font-600 cursor-pointer transition-colors ${
                          direction === dir
                            ? dir === 'Long' ? 'bg-positive text-background' : 'bg-negative text-white' :'bg-input text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <input type="radio" value={dir} {...register('direction')} className="sr-only" />
                        {dir}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Date + Timeframe */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-500 text-foreground mb-1.5">Trade Date <span className="text-negative">*</span></label>
                  <input
                    type="date"
                    {...register('date', { required: 'Date is required' })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-500 text-foreground mb-1.5">Timeframe</label>
                  <div className="relative">
                    <select
                      {...register('timeframe')}
                      className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/60 appearance-none transition-colors"
                    >
                      {timeframes.map((tf) => (
                        <option key={`tf-${tf}`} value={tf}>{tf}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Entry / Exit / Size */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-500 text-foreground mb-1.5">Entry Price <span className="text-negative">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('entryPrice', { required: 'Required' })}
                    placeholder="0.00"
                    className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60 font-mono-nums transition-colors"
                  />
                  {errors.entryPrice && <p className="text-xs text-negative mt-1">{errors.entryPrice.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-500 text-foreground mb-1.5">Exit Price <span className="text-negative">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('exitPrice', { required: 'Required' })}
                    placeholder="0.00"
                    className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60 font-mono-nums transition-colors"
                  />
                  {errors.exitPrice && <p className="text-xs text-negative mt-1">{errors.exitPrice.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-500 text-foreground mb-1.5">Position Size <span className="text-negative">*</span></label>
                  <input
                    type="number"
                    {...register('positionSize', { required: 'Required' })}
                    placeholder="Shares"
                    className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60 font-mono-nums transition-colors"
                  />
                </div>
              </div>

              {/* Stop Loss / Take Profit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-500 text-foreground mb-1.5">Stop Loss</label>
                  <p className="text-xs text-muted-foreground mb-1.5">Price level where you planned to exit if wrong</p>
                  <input
                    type="number"
                    step="0.01"
                    {...register('stopLoss')}
                    placeholder="0.00"
                    className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60 font-mono-nums transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-500 text-foreground mb-1.5">Take Profit</label>
                  <p className="text-xs text-muted-foreground mb-1.5">Target price level for this trade</p>
                  <input
                    type="number"
                    step="0.01"
                    {...register('takeProfit')}
                    placeholder="0.00"
                    className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60 font-mono-nums transition-colors"
                  />
                </div>
              </div>

              {/* Strategy */}
              <div>
                <label className="block text-xs font-500 text-foreground mb-1.5">Strategy Tag <span className="text-negative">*</span></label>
                <div className="relative">
                  <select
                    {...register('strategy', { required: 'Strategy is required' })}
                    className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/60 appearance-none transition-colors"
                  >
                    <option value="">Select strategy…</option>
                    {strategies.map((s) => (
                      <option key={`strategy-opt-${s}`} value={s}>{s}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
                {errors.strategy && <p className="text-xs text-negative mt-1">{errors.strategy.message}</p>}
              </div>

              {/* P&L Preview */}
              {pnl !== null && (
                <div className={`rounded-lg border px-4 py-3 ${parseFloat(pnl) >= 0 ? 'bg-positive-subtle border-positive/20' : 'bg-negative-subtle border-negative/20'}`}>
                  <p className="text-xs text-muted-foreground mb-0.5">Calculated P&L</p>
                  <p className={`text-xl font-bold font-mono-nums ${parseFloat(pnl) >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {parseFloat(pnl) >= 0 ? '+' : ''}${parseFloat(pnl).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              )}

              {/* Screenshot upload */}
              <div>
                <label className="block text-xs font-500 text-foreground mb-1.5">Chart Screenshots</label>
                <p className="text-xs text-muted-foreground mb-2">Attach entry and exit chart screenshots for later review</p>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const files = Array.from(e.dataTransfer.files);
                    const newScreenshots = files.slice(0, 3).map((f) => URL.createObjectURL(f));
                    setScreenshots((prev) => [...prev, ...newScreenshots].slice(0, 4));
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-150 ${
                    dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-muted/30'
                  }`}
                >
                  <Upload size={20} className="mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Drop screenshots here or <span className="text-primary">browse</span></p>
                  <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 10MB · Max 4 files</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const newScreenshots = files.slice(0, 3).map((f) => URL.createObjectURL(f));
                      setScreenshots((prev) => [...prev, ...newScreenshots].slice(0, 4));
                    }}
                  />
                </div>

                {screenshots.length > 0 && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {screenshots.map((src, i) => (
                      <div key={`screenshot-${i}`} className="relative w-20 h-14 rounded-lg overflow-hidden border border-border group">
                        <img src={src} alt={`Trade screenshot ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setScreenshots((prev) => prev.filter((_, idx) => idx !== i))}
                          className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={8} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="px-6 py-5 space-y-5">
              {/* Trade quality rating */}
              <div>
                <label className="block text-xs font-500 text-foreground mb-1.5">Trade Quality Rating</label>
                <p className="text-xs text-muted-foreground mb-2">How well did you execute your plan? (1 = poor, 5 = perfect)</p>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={`modal-star-${star}`}
                      type="button"
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(star)}
                      className="transition-transform hover:scale-110"
                    >
                      <Star
                        size={28}
                        className={`transition-colors ${
                          star <= (hoverRating || rating) ? 'text-warning fill-warning' : 'text-muted'
                        }`}
                      />
                    </button>
                  ))}
                  {rating > 0 && (
                    <span className="text-xs text-muted-foreground ml-2">
                      {['', 'Poor execution', 'Below average', 'Average', 'Good execution', 'Perfect execution'][rating]}
                    </span>
                  )}
                </div>
              </div>

              {/* Trade notes */}
              <div>
                <label className="block text-xs font-500 text-foreground mb-1.5">Trade Notes</label>
                <p className="text-xs text-muted-foreground mb-2">Describe the setup, your reasoning, and what happened</p>
                <textarea
                  {...register('notes')}
                  rows={4}
                  placeholder="e.g. Clean breakout above daily resistance on high volume. Entered on the 15m candle close above the level. Held through minor pullback."
                  className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60 resize-none transition-colors"
                />
              </div>

              {/* Lessons learned */}
              <div>
                <label className="block text-xs font-500 text-foreground mb-1.5">Lessons Learned</label>
                <p className="text-xs text-muted-foreground mb-2">What would you do differently? What did this trade teach you?</p>
                <textarea
                  {...register('lessons')}
                  rows={3}
                  placeholder="e.g. I should have waited for volume confirmation before entering. The setup was valid but timing was off by 30 minutes."
                  className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60 resize-none transition-colors"
                />
              </div>

              {/* Review summary */}
              <div className="bg-muted/50 rounded-xl p-4 border border-border">
                <p className="text-xs font-600 text-foreground mb-3">Trade Summary</p>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  {[
                    { label: 'Symbol', value: watch('symbol') || '—' },
                    { label: 'Direction', value: watch('direction') },
                    { label: 'Strategy', value: watch('strategy') || '—' },
                    { label: 'Entry', value: watch('entryPrice') ? `$${watch('entryPrice')}` : '—' },
                    { label: 'Exit', value: watch('exitPrice') ? `$${watch('exitPrice')}` : '—' },
                    { label: 'P&L', value: pnl ? `${parseFloat(pnl) >= 0 ? '+' : ''}$${parseFloat(pnl).toFixed(2)}` : '—' },
                  ].map((item) => (
                    <div key={`summary-${item.label}`}>
                      <p className="text-muted-foreground mb-0.5">{item.label}</p>
                      <p className={`font-500 font-mono-nums ${
                        item.label === 'P&L' && pnl
                          ? parseFloat(pnl) >= 0 ? 'text-positive': 'text-negative' :'text-foreground'
                      }`}>{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-border sticky bottom-0 bg-card">
            <button
              type="button"
              onClick={step === 1 ? handleClose : () => setStep(1)}
              className="px-4 py-2 rounded-lg text-sm font-500 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              {step === 1 ? 'Cancel' : '← Back'}
            </button>
            {step === 1 ? (
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-5 py-2 rounded-lg text-sm font-600 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-all duration-150"
              >
                Next: Add Notes →
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-600 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-150 min-w-[130px] justify-center"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Check size={14} />
                    Log Trade
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}